import React, { forwardRef } from "react";

type Variant =
  | "default"
  | "destructive"
  | "outline"
  | "secondary"
  | "ghost"
  | "link";
type Size = "default" | "sm" | "lg" | "icon";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  className?: string;
}

// Simple helper to merge classes
const cn = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(" ");

// Button variants (manual version of cva)
const getButtonClasses = (
  variant: Variant = "default",
  size: Size = "default"
) => {
  const base =
    "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-white transition-colors focus:outline-none outline-none focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50";

  const variants: Record<Variant, string> = {
    default:
      "bg-slate-900 text-slate-50 hover:bg-slate-900/90 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50/90",
    destructive:
      "bg-red-500 text-slate-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-slate-50 dark:hover:bg-red-900/90",
    outline:
      "border border-slate-200 bg-white hover:bg-slate-100 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-50",
    secondary:
      "bg-slate-100 text-slate-900 hover:bg-slate-100/80 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-800/80",
    ghost:
      "hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-slate-50",
    link: "text-slate-900 underline-offset-4 hover:underline dark:text-slate-50",
  };

  const sizes: Record<Size, string> = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
    icon: "h-10 w-10",
  };

  return `${base} ${variants[variant]} ${sizes[size]}`;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "default",
      size = "default",
      asChild = false,
      className,
      ...props
    },
    ref
  ) => {
    // AsChild: allows using <a> or <div> as the root element
    const Component: any = asChild ? "span" : "button";

    return (
      <Component
        ref={ref}
        className={cn(getButtonClasses(variant, size), className)}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";
